import { ChatArtifact, ChatObjectType, chatState, ObjectEditorTempArtifactValues, ObjectEditorTempTagValues, ObjectEditorTempEventValues } from "@/app/chatState";
import { draftsState } from "@/app/draftsState";
import { observer } from "mobx-react-lite";
import React, { JSX, memo, ReactNode, use, useCallback, useEffect, useMemo, useState } from "react";
import ArtifactEntry from "../../artifacts/entry";
import { Request } from "@/components/CRUD";
import { artifact, artifact_revision } from "@/app/typing/artifacts";
import { ActionHandler, EntryItem, useRefState } from "@/components/UI/tools";
import { adminCreateArtifact, AdminCreateArtifactData, adminDeleteArtifact, AdminDeleteArtifactData, adminGetArtifact, AdminGetArtifactData, adminUpdateArtifact, AdminUpdateArtifactData, chatUpdateArtifact, chatDeleteObjects } from "@/api";
import { appState } from "@/app/appState";
import { editRequestWebSocket } from "@/aawebsocket/websocket.gen";
import { applyDiffs } from "@/components/diffs";
import { keys, set } from "mobx";
import { runInAction } from "mobx";
import { tag_revision } from "@/app/typing/tags";
import { calendar_event_revision } from "@/app/typing/events";
import { Org, Remove, User } from "@/components/UI/svgs";
import { computeUserInputChangesOnArtifact } from "../chatContainer";
import { createPatch } from 'diff';
import { ArtifactEditor } from "./objectEditors/artifactEditor";
import { TagEditor } from "./objectEditors/tagEditor";
import { EventEditor } from "./objectEditors/eventEditor";
import { DocumentEditor } from "./objectEditors/documentEditor";
import { NavBarGroup } from "@/components/navbar/workspaceNavbar";
import { isSourceType } from "../artifactSource";
import { containNestedScroll } from "@/components/scrollUtils";

type objectTab = {
    key: { type: ChatObjectType, id: number }
    label: string
    revisions?: artifact_revision[] | tag_revision[] | calendar_event_revision[]
}

export const shortenLabel = (label: string, maxLength: number = 15): string => {
    if (label?.length > maxLength) {
        return label.substring(0, maxLength - 3) + '...';
    }
    return label;
}

export const TabItem = observer(({
    children,
    onClick,
    isSelected,
    isFocused,
    isDisabled,
    revisions,
    revisionKey,
    isChild,
}: { children: ReactNode, onClick: (e: React.MouseEvent) => void, isSelected: boolean, isFocused?: boolean, isDisabled: boolean, revisions?: artifact_revision[] | tag_revision[] | calendar_event_revision[], revisionKey?: { type: ChatObjectType, id: number }, isChild?: boolean }): JSX.Element => {
    const activeRevisionKey = revisionKey ?? chatState.selectedObjectKey!;
    const maxRevPage = revisions ? revisions.filter(x => x !== undefined)?.length : 0
    const revisionPage = chatState.chatObjects.get(activeRevisionKey!)?.revision_index ?? -1
    return (
        <ActionHandler
            funq={(e) => onClick(e!)}
            title={children as string}
            selected={isSelected}
            className={`btn-ghost${isFocused && !isSelected ? " editor-tab-focused" : ""}${isFocused && isSelected ? " editor-tab-focused-selected" : ""}`}
        >
            <div className="flex flex-row items-center">

                <small>{<b>{shortenLabel(children as string)}</b>}</small>
                {!isDisabled && maxRevPage > 0 && (
                    <div className=" flex flex-row justify-between h-fit">
                        <small className=" flex flex-row gap-1 pl-1">
                            <div onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                                if (revisionPage > 0)
                                    chatState.chatObjects.update(activeRevisionKey ? [{ key: activeRevisionKey, value: { revision_index: revisionPage - 1 } }] : [])
                                else
                                    chatState.chatObjects.update(activeRevisionKey ? [{ key: activeRevisionKey, value: { revision_index: -1 } }] : [])

                                e.stopPropagation();
                            }} className="editor-tab-step">
                                {'<'}
                            </div>
                            <div>
                                <div>
                                    {(revisionPage == -1 ? '-' : (revisionPage + 1)) + ' / ' + (maxRevPage)}
                                </div>
                            </div>
                            <div onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                                if (revisionPage < maxRevPage - 1)
                                    chatState.chatObjects.update(activeRevisionKey ? [{ key: activeRevisionKey, value: { revision_index: revisionPage + 1 } }] : [])


                                e.stopPropagation();

                            }} className="editor-tab-step">
                                {'>'}
                            </div>
                        </small>
                    </div>
                )}
            </div>

        </ActionHandler>
    );
})

const ObjectTabs = observer(({ }: {}) => {
    const [deleting, setDeleting] = useState(false);
    // Show a tab for any object that is selected, plus any source (event or
    // document) that has at least one selected note.
    const tabs = [...chatState.chatObjects.entries()].filter(x => {
        if (x.value.isSelected) return true;
        if (isSourceType(x.key.type)) {
            const node = chatState.chatObjects.entries([x.key as any])[0];
            return (node?.children ?? []).some((c: any) => chatState.chatObjects.get(c.key)?.isSelected);
        }
        return false;
    }).map(x => ({ key: x.key, label: x.value.label, revisions: x.value.revisions?.filter(x => x !== undefined) } as objectTab))
    // UGLY AF DO BIG REFACTOR BACKEND TO FIX THIS GARBAGE CODE SMELL
    const deleteSelected = async () => {
        const selected = chatState.getSelected();
        if (selected.length === 0 || chatState.conversationId === null) return;

        setDeleting(true);
        try {
            const result = await chatDeleteObjects({
                body: {
                    conversation_id: chatState.conversationId,
                    objects: selected.map(x => ({ type: x.type as 'artifact' | 'tag' | 'event' | 'document', id: x.id })),
                },
            });

            if (result.error) {
                appState.setModal({
                    title: appState.t("common.error"),
                    body: appState.t("chat.deleteSelectedError"),
                });
            } else {
                runInAction(() => {
                    const artifactIds = selected.filter(x => x.type === "artifact").map(x => x.id);
                    const tagIds = selected.filter(x => x.type === "tag").map(x => x.id);
                    const eventIds = selected.filter(x => x.type === "event").map(x => x.id);
                    const documentIds = selected.filter(x => x.type === "document").map(x => x.id);
                    if (artifactIds.length > 0)
                        appState.artifacts = appState.artifacts?.filter(x => !artifactIds.includes(x.id));
                    if (tagIds.length > 0)
                        appState.tags = appState.tags?.filter(x => !tagIds.includes(x.id));
                    if (eventIds.length > 0)
                        appState.events = appState.events?.filter(x => !eventIds.includes(x.id));
                    if (documentIds.length > 0)
                        appState.documents = appState.documents?.filter(x => !documentIds.includes(x.id));
                });
                chatState.forgetDeletedObjects(selected.map(x => ({ type: x.type as ChatObjectType, id: x.id })));
                draftsState.remove((result.data as { deleted?: { type: string; id: number }[] } | undefined)?.deleted ?? selected);
                chatState.setSelectedObjectKey(null);
            }
        } finally {
            setDeleting(false);
        }
    };

    const confirmDeleteSelected = () => {
        const selected = chatState.getSelected();
        const selectedDetails = selected.map(x => {
            const obj = chatState.chatObjects.get(x);
            return { type: x.type, label: obj?.label ?? `${appState.t(`chat.objectType.${x.type}`)} #${x.id}` };
        });

        appState.setModal({
            title: appState.t("common.confirmTitle"),
            body: (
                <div>
                    <p className="mb-2">{appState.t("chat.deleteSelectedIntro")}</p>
                    <ul className="list-disc list-inside space-y-1">
                        {selectedDetails.map((x, i) => (
                            <li key={i}>
                                <span className="capitalize opacity-60">{appState.t(`chat.objectType.${x.type}`)}</span>: <b>{x.label}</b>
                            </li>
                        ))}
                    </ul>
                </div>
            ),
            action: deleteSelected,
        });
    };

    return (
        <div className="editor-tabs">

            {tabs?.map(x => {
                const isRootSelected = chatState.selectedObjectKey?.id == x.key.id && chatState.selectedObjectKey?.type == x.key.type;
                const rootObj = chatState.chatObjects.get(x.key);
                const childIndex = (rootObj as any)?.child_index;

                // For source tabs: enumerate all children so they're always visible
                const sourceChildren: { key: { type: ChatObjectType, id: number }, label: string, revisions: any[] }[] = [];
                if (isSourceType(x.key.type)) {
                    const treeNodes = chatState.chatObjects.entries([x.key as any]);
                    const treeNode = treeNodes.length > 0 ? treeNodes[0] : undefined;
                    (treeNode?.children ?? []).forEach((child: any, idx: number) => {
                        const childObj = chatState.chatObjects.get(child.key);
                        sourceChildren.push({
                            key: child.key,
                            label: childObj?.label ?? appState.t(`chat.objectType.${child.key.type}`),
                            revisions: (childObj?.revisions ?? []).filter((r: any) => r !== undefined),
                        });
                    });
                }

                return (
                    <React.Fragment key={`${x.key.type}-${x.key.id}`}>
                        {sourceChildren.length > 0 ? (
                            <NavBarGroup>
                                <TabItem
                                    onClick={(e) => {
                                        // Clicking the source tab itself clears the child sub-selection
                                        chatState.chatObjects.update([{ key: x.key as any, value: { child_index: undefined } }]);
                                        if (e.ctrlKey) {
                                            chatState.setSelectedObjectKey(x.key, true, false, rootObj?.isSelected ?? false);
                                        } else {
                                            chatState.setSelectedObjectKey(x.key, false);
                                        }
                                    }}
                                    isSelected={rootObj?.isSelected ?? false}
                                    isFocused={isRootSelected && childIndex === undefined}
                                    isDisabled={!(isRootSelected && (!isSourceType(x.key.type) || childIndex === undefined))}
                                    revisions={(!isSourceType(x.key.type) || childIndex === undefined) ? x.revisions : undefined}
                                >
                                    {x.label}
                                </TabItem>
                                {sourceChildren.map((child, idx) => {
                                    const isChildFocused = isRootSelected && childIndex === idx;
                                    const childIsGlowing = chatState.chatObjects.get(child.key)?.isSelected ?? false;
                                    return (
                                        <TabItem
                                            onClick={(e) => {
                                                if (e.ctrlKey) {
                                                    const currentlySelected = chatState.chatObjects.get(child.key)?.isSelected ?? false;
                                                    chatState.setSelectedObjectKey(x.key, true, false, false, child.key, false);
                                                    chatState.chatObjects.update([{ key: child.key, value: { isSelected: !currentlySelected } }, { key: x.key as any, value: { child_index: idx } }]);
                                                } else {
                                                    chatState.setSelectedObjectKey(x.key, false, false, false, child.key, false);
                                                    chatState.chatObjects.update([{ key: child.key, value: { isSelected: true } }, { key: x.key as any, value: { child_index: idx } }]);
                                                }
                                            }}
                                            isSelected={childIsGlowing}
                                            isFocused={isChildFocused}
                                            isDisabled={!isChildFocused}
                                            revisions={isChildFocused ? child.revisions as any : undefined}
                                            revisionKey={child.key}
                                            isChild
                                        >
                                            {child.label}
                                        </TabItem>
                                    );
                                })}
                            </NavBarGroup>
                        ) : (
                            <div className={"flex flex-row items-center p-[0.25rem]"}>
                                <TabItem
                                    onClick={(e) => {
                                        chatState.chatObjects.update([{ key: x.key as any, value: { child_index: undefined } }]);
                                        if (e.ctrlKey) {
                                            chatState.setSelectedObjectKey(x.key, true, false, rootObj?.isSelected ?? false);
                                        } else {
                                            chatState.setSelectedObjectKey(x.key, false);
                                        }
                                    }}
                                    isSelected={rootObj?.isSelected ?? false}
                                    isFocused={isRootSelected}
                                    isDisabled={!(isRootSelected && (!isSourceType(x.key.type) || childIndex === undefined))}
                                    revisions={x.revisions}
                                >
                                    {x.label}
                                </TabItem>

                            </div>)}
                    </React.Fragment>
                )
            })}
            <div className={"w-full flex flex-row items-center justify-end p-[0.25rem] pr-2"}>
                <ActionHandler
                    title={appState.t("chat.deleteSelected")}
                    keysTrigger="Delete"
                    funq={async () => confirmDeleteSelected()}
                    disabled={deleting || chatState.getSelected().length === 0}
                    className=""
                >
                    <Remove />
                </ActionHandler>
            </div>
        </div>
    )
})

export const removeEmptyLikeFields = (obj: any) => {
    return Object.fromEntries(Object.entries(obj ?? {}).filter(([_, v]) => v !== undefined || (Array.isArray(v) && (v as any).length > 0)))
}

export const setHasChagedCallback = (old_obj: any) => {

    chatState.setHasChangedCallback((new_obj) => {
        switch (new_obj.type) {
            case "artifact":

                new_obj = { ...old_obj, ...removeEmptyLikeFields(new_obj) } as ObjectEditorTempArtifactValues
                const new_content_trimmed = (new_obj.content ?? "").replace(/\s+$/gm, "");
                const old_content_trimmed = old_obj.content.replace(/\s+$/gm, "");

                return (
                    new_obj.variable_name !== old_obj.variable_name ||
                    new_content_trimmed !== old_content_trimmed ||
                    new_obj.event_id !== old_obj.event_id ||
                    new_obj.document_id !== old_obj.document_id ||
                    JSON.stringify(new_obj.tag_ids?.sort() ?? []) !== JSON.stringify(old_obj.tag_ids?.sort() ?? [])
                )
            case "tag":
                new_obj = { ...old_obj, ...removeEmptyLikeFields(new_obj) } as ObjectEditorTempTagValues

                const new_description_trimmed = (new_obj.description ?? "").replace(/\s+$/gm, "");
                const old_description_trimmed = old_obj.description.replace(/\s+$/gm, "");


                return (
                    new_obj.name !== old_obj.name ||
                    new_description_trimmed !== old_description_trimmed ||
                    new_obj.scope !== old_obj.scope ||
                    new_obj.permission_type !== old_obj.permission_type ||
                    new_obj.attributes !== old_obj.attributes ||
                    JSON.stringify(new_obj.tpr_organisation_ids?.sort() ?? []) !== JSON.stringify(old_obj.tpr_organisation_ids?.sort() ?? [])
                )
            case "event":
                new_obj = { ...old_obj, ...removeEmptyLikeFields(new_obj) } as ObjectEditorTempEventValues

                const new_description_trimmed_event = (new_obj.description ?? "").replace(/\s+$/gm, "");
                const old_description_trimmed_event = (old_obj.description ?? "").replace(/\s+$/gm, "");

                return (
                    new_obj.title !== old_obj.title ||
                    new_description_trimmed_event !== old_description_trimmed_event ||
                    new_obj.location !== old_obj.location ||
                    new_obj.start_time !== old_obj.start_time ||
                    new_obj.end_time !== old_obj.end_time ||
                    new_obj.all_day !== old_obj.all_day ||
                    new_obj.timezone !== old_obj.timezone
                )
        }
        // trim all trailing whitespace from contentDiff
    });
}



export const ObjectEditorWindow = observer(({ }: {}) => {
    const panelRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = panelRef.current;
        if (!el) return;
        return containNestedScroll(el);
    }, []);

    const isEventEditor = chatState.selectedObjectKey?.type === "event";

    return (
        <div ref={panelRef} className="w-full h-full min-h-0 flex flex-col justify-center">
            <div className={`w-full min-h-0 max-h-full pt-1 overflow-hidden transition-all ease-in-out duration-200 flex flex-col pb-[3.75rem] items-center`}>
                <div className="shrink-0 w-full">
                    <ObjectTabs />
                </div>
                <div className={`panel-elevated overscroll-none flex-1 min-h-0 w-full ${isEventEditor ? "flex flex-col-reverse overflow-y-auto" : "overflow-y-auto"}`}>
                    <div className={isEventEditor ? "flex w-full flex-col" : "flex flex-col h-full min-h-0"}>
                        <div
                            className={isEventEditor ? "w-full" : "w-full h-full min-h-0 overflow-y-auto overscroll-none"}
                            // Stable scope marker so citation-click can locate the exact
                            // rendered object's content (avoids matching cited_text inside
                            // the chat transcript, which also renders the citation).
                            data-object-content={
                                chatState.selectedObjectKey
                                    ? `${chatState.selectedObjectKey.type}-${chatState.selectedObjectKey.id}`
                                    : undefined
                            }
                        >
                            {chatState.selectedObjectKey?.type == "artifact" ? <ArtifactEditor /> : null}
                            {chatState.selectedObjectKey?.type == "tag" ? <TagEditor /> : null}
                            {chatState.selectedObjectKey?.type == "event" ? <EventEditor /> : null}
                            {chatState.selectedObjectKey?.type == "document" ? <DocumentEditor /> : null}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
})